import { useState } from 'react'
import { Form, Formik, FormikHelpers } from 'formik'
import { useToast } from '@/hooks/useToast'
import { useRouter } from 'next/router'

/* blocks */
import Button from '@/atoms/Button/Button'
import TextField from '@/molecules/FormikFields/TextField'
import PhoneField from '@/molecules/FormikFields/PhoneField'
import SelectField from '@/molecules/FormikFields/SelectField'
import CheckboxField from '@/molecules/FormikFields/CheckboxField'

/* utils */
import { numEmployeesOptions } from '@/utils/constants/form'
import { dataLayerFormSubmit } from '@/utils/functions/dataLayerEvents'
import {
  initialFormValues,
  ResourceFormValidationSchema,
  ResourceFormValues
} from './ResourceForm.schema'

interface Props {
  formName: string
  title: string | null
  description: string | null
  ctaTitle: string
  successMessage: string
}

function ResourceForm({
  formName,
  title,
  description,
  ctaTitle,
  successMessage
}: Props): JSX.Element {
  const [, setIsFormSubmitted] = useState<boolean>(false)
  const [isFormLoading, setIsFormLoading] = useState<boolean>(false)
  const { toast, showToast } = useToast()
  const router = useRouter()

  const handleFormSubmit = async (
    values: ResourceFormValues,
    helpers: FormikHelpers<ResourceFormValues>
  ) => {
    setIsFormLoading(true)
    try {
      const result = await fetch('/api/forms/resources/', {
        method: 'POST',
        body: JSON.stringify({
          ...values,
          sourceUrl: router.asPath,
          status: 'subscribed'
        }),
        headers: {
          'Content-Type': 'application/json'
        }
      })

      const data = await result.json()

      if (!result.ok) {
        showToast({
          title: data.message,
          type: 'error',
          swipeDirection: 'up'
        })
        return
      }
      dataLayerFormSubmit({
        formName: `Resources - ${formName}`,
        formValues: values
      })
      setIsFormSubmitted(true)
      helpers.resetForm()
      showToast({
        title: successMessage ?? 'Thank you for your interest!',
        type: 'success',
        swipeDirection: 'up'
      })
    } catch (e) {
      showToast({
        title: 'Please try again!',
        type: 'error',
        swipeDirection: 'up'
      })
    } finally {
      setIsFormLoading(false)
    }
  }

  return (
    <>
      <Formik<ResourceFormValues>
        initialValues={initialFormValues}
        validationSchema={ResourceFormValidationSchema}
        onSubmit={handleFormSubmit}
      >
        <Form
          id="resource-form"
          name="Resource Form"
          className="bg-cyan-10  | md:rounded-2xl  |  py-18 px-4 "
        >
          <header className="text-center  |  max-w-xl mx-auto">
            {title && <h2 className="h4 font-medium  |  mb-2">{title}</h2>}
            {description && (
              <p className="text-paragraph-2 text-gray-70  |  mb-8">
                {description}
              </p>
            )}{' '}
          </header>
          <div className="max-w-md | mx-auto space-y-4">
            <TextField name="firstName" label="First Name" required />
            <TextField name="lastName" label="Last Name" required />
            <TextField name="company" label="Company" required />
            <TextField name="designation" label="Designation" required />
            <TextField name="email" label="Email Address" required />
            <PhoneField
              name="phone"
              label="Contact Number"
              className="mb-2 relative z-10"
              required
            />
            <SelectField
              name="numEmployees"
              label="Number of Employees"
              placeholder="Select"
              options={numEmployeesOptions.map(item => ({
                name: item.name,
                value: item.value
              }))}
              className="relative mb-4"
              required
            />
            <CheckboxField
              name="contact_consent"
              label="I'd like expert help to create my content plan"
            />
            <CheckboxField
              name="terms_conditions"
              label={
                <span>
                  I agree to
                  <a
                    className="text-blue-50"
                    href="https://www.peppercontent.io/terms"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Terms and conditions
                  </a>
                </span>
              }
              required
            />

            <Button
              className="mt-6 mx-auto capitalize w-full"
              loading={isFormLoading}
              htmlType="submit"
            >
              {ctaTitle}
            </Button>
          </div>
        </Form>
      </Formik>
      {toast}
    </>
  )
}

export default ResourceForm
